import {
  AlertCircle,
  Calendar,
  CalendarDays,
  CheckSquare,
  Flag,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { PersonalStats } from "../types";

type Props = {
  stats: PersonalStats;
};

type Card = {
  key: keyof PersonalStats;
  label: string;
  icon: typeof CheckSquare;
  color: string;
  bg: string;
  href: string;
};

const CARDS: Card[] = [
  {
    key: "openTasks",
    label: "Open Tasks",
    icon: CheckSquare,
    color: "hsl(210,100%,40%)",
    bg: "hsl(210,55%,95%)",
    href: "/tasks?assignedToMe=true",
  },
  {
    key: "openActions",
    label: "Open Actions",
    icon: Zap,
    color: "hsl(35,90%,35%)",
    bg: "hsl(38,90%,94%)",
    href: "/actions?assignedToMe=true",
  },
  {
    key: "meetingsToday",
    label: "Meetings Today",
    icon: CalendarDays,
    color: "hsl(262,83%,45%)",
    bg: "hsl(262,55%,95%)",
    href: "/meetings",
  },
  {
    key: "meetingsThisWeek",
    label: "Meetings This Week",
    icon: Calendar,
    color: "hsl(180,72%,30%)",
    bg: "hsl(180,55%,94%)",
    href: "/meetings",
  },
  {
    key: "overdueItems",
    label: "Overdue Items",
    icon: AlertCircle,
    color: "hsl(0,70%,40%)",
    bg: "hsl(0,70%,96%)",
    href: "/tasks?overdue=true",
  },
  {
    key: "openMilestones",
    label: "Open Milestones",
    icon: Flag,
    color: "hsl(142,72%,28%)",
    bg: "hsl(142,55%,94%)",
    href: "/milestones",
  },
];

export function QuickStatsWidget({ stats }: Props) {
  return (
    <div
      className="grid gap-3"
      style={{
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      }}
    >
      {CARDS.map((c) => {
        const count = stats[c.key] ?? 0;
        const isOverdueCard = c.key === "overdueItems";
        const highlight = isOverdueCard && count > 0;
        return (
          <Link
            key={c.key}
            to={c.href}
            className="rounded-xl border bg-white p-3 transition-colors hover:shadow-sm"
            style={{
              borderColor: highlight
                ? "hsl(0,70%,85%)"
                : "hsl(210,30%,85%)",
              background: highlight ? "hsl(0,70%,98%)" : "white",
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-md"
                style={{ background: c.bg, color: c.color }}
              >
                <c.icon className="h-4 w-4" strokeWidth={1.5} />
              </span>
              <div className="min-w-0">
                <div
                  className="text-[28px] font-bold leading-none"
                  style={{
                    color:
                      count === 0
                        ? "hsl(215,16%,60%)"
                        : highlight
                          ? "hsl(0,70%,40%)"
                          : "hsl(215,25%,27%)",
                  }}
                >
                  {count}
                </div>
              </div>
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              {c.label}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
